import { useTranslations } from 'next-intl'

import { TContractStatus, TRefundStatus } from '@/entities/contract'

import { useFormatDateTime } from '../../lib/date'

const STEP_2 = 1
const STEP_2_COMPLETE = 2
const STEP_3 = 2
const STEP_4_COMPLETE = 4
const STEP_6 = 5
const STEP_6_COMPLETE = 6

export const getCurrentStep = (
  contractStatus: TContractStatus,
  refundStatus: TRefundStatus | null,
) => {
  if (contractStatus === 'APPROVE_READY') return STEP_2
  if (contractStatus === 'CANCEL') return STEP_2_COMPLETE
  if (contractStatus === 'PAY_READY') return STEP_3
  if (contractStatus === 'CONTRACT_COMPLETE') return STEP_4_COMPLETE
  if (refundStatus === 'REFUND_READY') return STEP_6
  if (refundStatus === 'REFUND_COMPLETE') return STEP_6_COMPLETE

  return 0
}

export const useCurrentStepLabel = (
  contractStatus: TContractStatus,
  refundStatus: TRefundStatus | null,
) => {
  const t = useTranslations('lease_detail.lease_status')

  if (contractStatus === 'APPROVE_READY') return t('approval_pending')
  if (contractStatus === 'CANCEL') return t('lease_cancel')
  if (contractStatus === 'PAY_READY') return t('payment_pending')
  if (contractStatus === 'CONTRACT_COMPLETE') return t('lease_confirm')
  if (refundStatus === 'REFUND_READY') return t('refund_pending')
  if (refundStatus === 'REFUND_COMPLETE') return t('refund_confirm')
}

type Step = {
  label: string
  time: string | null
}

export type Props = {
  currentStep: number
  contractStatus: TContractStatus
  contractRequestTime: string
  approveTime: string | null
  contractCompleteTime: string | null
  cancelTime: string | null
  closedTime: string | null
}

export const useStepLabel = ({
  currentStep,
  contractStatus,
  contractRequestTime,
  approveTime,
  contractCompleteTime,
  cancelTime,
  closedTime,
}: Props) => {
  const t = useTranslations('lease_detail.lease_status')
  const { formatDateTime } = useFormatDateTime()

  // STEP 1

  const step: Step[] = [
    {
      label: t('approval_request'),
      time: formatDateTime(contractRequestTime),
    },
  ]

  // STEP 2

  if (contractStatus === 'CANCEL') {
    step.push({
      label: t('lease_cancel'),
      time: formatDateTime(closedTime),
    })

    return step
  }

  if (contractStatus === 'APPROVE_READY') {
    step.push({
      label: t('approval_pending'),
      time: null,
    })
  }

  if (currentStep > STEP_2) {
    step.push({
      label: t('approved'),
      time: formatDateTime(approveTime),
    })
  }

  // STEP 3

  if (currentStep <= STEP_3) {
    step.push({
      label: t('payment_pending'),
      time: null,
    })
  }

  if (currentStep > STEP_3) {
    step.push({
      label: t('payment_complete'),
      time: formatDateTime(contractCompleteTime),
    })
  }

  // STEP 4

  if (currentStep < STEP_4_COMPLETE) {
    step.push({
      label: t('lease_confirm'),
      time: null,
    })
  }

  if (currentStep >= STEP_4_COMPLETE) {
    step.push({
      label: t('lease_confirm'),
      time: formatDateTime(contractCompleteTime),
    })
  }

  if (currentStep <= STEP_4_COMPLETE) {
    return step
  }

  // STEP 5

  step.push({
    label: t('lease_cancel'),
    time: formatDateTime(cancelTime),
  })

  // STEP 6

  if (currentStep <= STEP_6) {
    step.push({
      label: t('refund_pending'),
      time: null,
    })
  }

  if (currentStep > STEP_6) {
    step.push({
      label: t('refund_confirm'),
      time: formatDateTime(closedTime),
    })
  }

  return step
}
